/**
 * Opens HitPay's hosted checkout in the system in-app browser
 * (SFSafariViewController on iOS, Chrome Custom Tabs on Android) and resolves
 * once the user comes back.
 *
 * IMPORTANT: neither outcome tells you whether the payment succeeded. A user can
 * pay and then kill the browser before the redirect fires, and HitPay's redirect
 * "can be triggered by anyone" per their own docs. Callers MUST poll the ledger
 * in both cases — the ledger is the only source of truth for payment status.
 */
import { Browser } from '@capacitor/browser';
import { App as CapApp } from '@capacitor/app';
import { isNativeNotWeb } from '@/utils/platform-check';

// Exported so the global appUrlOpen listener in App.vue can recognise — and ignore —
// this callback instead of running it through classifyDeepLink. Also the host that
// android/app/src/main/AndroidManifest.xml must register an intent filter for.
export const RETURN_URL_PREFIX = 'app.mobile.hogc://payment-return';

export const HitPayCheckoutResult = {
  RETURNED: 'returned',
  DISMISSED: 'dismissed',
};

/**
 * Web-only: how long we wait for this document to go hidden before concluding
 * that nothing actually opened (popup blocked, or the browser kept us focused).
 * Short, because until it elapses the sheet is showing a spinner.
 */
const WEB_HANDOFF_TIMEOUT_MS = 1500;

/** Web-only: hard cap on waiting for the member to come back to our tab. */
const WEB_RETURN_TIMEOUT_MS = 10 * 60 * 1000;

/**
 * Web-only: how often to ask the checkout window whether it has been closed.
 * `closed` is a property, not an event, so it has to be polled. Cheap.
 */
const WEB_CLOSED_POLL_MS = 500;

/**
 * Web-only: resolve once the checkout is closed.
 *
 * Web has neither `browserFinished` nor a custom scheme for `appUrlOpen`. What
 * it does have is the window handle `window.open` returns, and that handle's
 * `closed` flag is the one honest "the checkout is gone" signal on this
 * platform — readable cross-origin, and true the moment the tab or popup goes.
 *
 * Page visibility is NOT that signal, and treating it as one was the review
 * finding on the first cut: a `_blank` checkout can land in a separate window
 * or a background tab while this document stays `visible`, so "we never went
 * hidden" does not mean "nothing opened", and "we are visible again" does not
 * mean "it closed" — a member can flick back to our tab with the checkout still
 * open beside it. So when there IS a handle, only `closed` ends the wait.
 *
 * Visibility remains as the fallback for a browser that opens the checkout but
 * hands back no window — `null` is what a blocked popup looks like, and some
 * mobile browsers return it for a tab they did open. There, going hidden means
 * something opened and coming back means the member is done with it.
 *
 * Why it matters that we wait at all: HitPay's hosted checkout lets a member
 * re-enter a card after a decline, and each attempt fires its own webhook. If we
 * start polling the ledger while that tab is still open, we read the FIRST
 * attempt's FAILED and tell the member their payment failed while they are still
 * paying in front of us. Native never had this problem — it waits on
 * `browserFinished`/`appUrlOpen`, which is exactly this signal. Web was the odd
 * one out.
 *
 * Every timeout falls back to resolving rather than hanging: a sheet stuck on
 * "Processing" forever is worse than a poll that starts early.
 */
function waitForWebReturn(checkoutWindow) {
  return new Promise(resolve => {
    let settled = false;
    let handoffTimer = null;
    let returnTimer = null;
    let closedPoll = null;
    // Declared as hoisted function declarations, not consts: `finish` unsubscribes
    // these two and both of them call `finish`, so in any writing order one
    // reference points forwards. Hoisting makes that read cleanly instead of
    // resting on the fact that no call happens until every const is initialised.
    function onVisibilityChange() {
      if (document.visibilityState === 'hidden') {
        startWaitingForReturn();
        return;
      }
      finish();
    }

    // Belt and braces: a same-tab navigation away and back can restore
    // visibility without firing visibilitychange in every browser.
    function onFocus() {
      if (document.visibilityState !== 'hidden') finish();
    }

    function finish() {
      if (settled) return;
      settled = true;
      clearTimeout(handoffTimer);
      clearTimeout(returnTimer);
      clearInterval(closedPoll);
      document.removeEventListener('visibilitychange', onVisibilityChange);
      window.removeEventListener('focus', onFocus);
      resolve(HitPayCheckoutResult.DISMISSED);
    }

    // The handoff happened: from here we wait for the member, not for a tab to
    // open, so the short timer must not fire under them.
    function startWaitingForReturn() {
      if (returnTimer) return;
      clearTimeout(handoffTimer);
      returnTimer = setTimeout(finish, WEB_RETURN_TIMEOUT_MS);
    }

    if (checkoutWindow) {
      // A handle: wait for the window itself, and nothing else. No visibility
      // listeners at all, so a flick back to this tab cannot end the wait while
      // the checkout is still open beside it. The hard cap still applies.
      returnTimer = setTimeout(finish, WEB_RETURN_TIMEOUT_MS);
      closedPoll = setInterval(() => {
        if (checkoutWindow.closed) finish();
      }, WEB_CLOSED_POLL_MS);
      return;
    }

    document.addEventListener('visibilitychange', onVisibilityChange);
    window.addEventListener('focus', onFocus);

    if (document.visibilityState === 'hidden') {
      // Already backgrounded before we opened — there is no handoff left to
      // wait for, only the member's return.
      startWaitingForReturn();
    } else {
      // Nothing took focus away from us, so there is no checkout tab to wait
      // for. Hand back, which is what this function used to do outright.
      handoffTimer = setTimeout(finish, WEB_HANDOFF_TIMEOUT_MS);
    }
  });
}

export async function openHitPayCheckout({ url }) {
  // On web the checkout opens in another tab or window. Opened directly rather
  // than through @capacitor/browser: its web open() is this same window.open
  // call but throws the handle away, and the handle is what tells us when the
  // checkout has closed. No subscribe-before-open dance is needed here —
  // `closed` is state, not an event, and the visibility fallback's first
  // `visibilitychange` cannot dispatch until this synchronous call has returned.
  if (!isNativeNotWeb()) {
    return waitForWebReturn(window.open(url, '_blank'));
  }

  let settle;
  const outcome = new Promise(resolve => {
    settle = resolve;
  });

  let settled = false;
  let listeners = [];

  const finish = result => {
    if (settled) return;
    settled = true;
    listeners.forEach(listener => listener.remove());
    settle(result);
  };

  // Subscribe BEFORE opening: on a fast return the deep link can fire while
  // Browser.open is still resolving, and a listener added afterwards misses it.
  listeners = await Promise.all([
    CapApp.addListener('appUrlOpen', ({ url: openedUrl }) => {
      // The app has other appUrlOpen listeners (Auth0 logout) — only claim ours.
      if (!openedUrl?.startsWith(RETURN_URL_PREFIX)) return;
      Browser.close().catch(() => {});
      finish(HitPayCheckoutResult.RETURNED);
    }),
    Browser.addListener('browserFinished', () => finish(HitPayCheckoutResult.DISMISSED)),
  ]);

  try {
    await Browser.open({ url });
  } catch (error) {
    // Nothing opened, so nothing will ever settle the promise — tear down first.
    listeners.forEach(listener => listener.remove());
    settled = true;
    throw error;
  }

  return outcome;
}
